<template>
  <div class="alice-shell theme-turquoise">
    <TopBar class="alice-shell__topbar">
      <template #breadcrumb>
        <a
          href="#"
          class="alice-tb-crumb"
          @click.prevent="$router.push('/projects')"
          >Projects</a
        >
        <span class="alice-tb-sep">›</span>
        <span class="alice-tb-crumb alice-tb-crumb--current">My profile</span>
      </template>
    </TopBar>

    <Sidebar class="alice-shell__sidebar" variant="rail" active-key="" />

    <main class="alice-shell__main">
      <div class="alice-prof">
        <header class="alice-prof__head">
          <div class="alice-prof__head-main">
            <div class="alice-prof__crumb">
              <span>Workspace</span>
              <Icon name="chevron-right" :size="12" />
              <span>My profile</span>
            </div>
            <h1 class="alice-prof__title">My profile</h1>
            <p class="alice-prof__subtitle">
              How you appear inside the workspace. Job title is shown next to
              your name on projects you own.
            </p>
          </div>

          <button
            class="alice-prof__save"
            :disabled="saving || !dirty"
            @click="updateUser"
          >
            <Icon name="plus" :size="16" />
            <span>{{ saving ? "Saving…" : "Save changes" }}</span>
          </button>
        </header>

        <section class="alice-prof__identity">
          <Avatar
            :name="userName"
            :photo="userPhoto"
            :color="ownerColor"
            :size="72"
            ring
          />
          <div class="alice-prof__identity-text">
            <div class="alice-prof__identity-name">{{ userName || "—" }}</div>
            <div class="alice-prof__identity-email">{{ user.email }}</div>
          </div>
        </section>

        <section class="alice-prof__form">
          <div class="alice-prof__field">
            <label for="prof-job" class="alice-prof__label">Job title</label>
            <input
              id="prof-job"
              v-model="job_title"
              class="alice-prof__input"
              type="text"
              placeholder="e.g. UX Researcher"
              autocomplete="organization-title"
            />
            <p class="alice-prof__help">
              Visible on project cards and reports you author.
            </p>
          </div>
        </section>

        <section v-if="!isGuest && rulebook" class="alice-prof__rules">
          <div class="alice-prof__usage-head">
            <div class="alice-prof__usage-head-main">
              <div class="alice-prof__label">How Alice writes for you</div>
              <p class="alice-prof__help">
                Alice remembers the preferences you express in chat ("shorter",
                "no bullets", "always in Dutch") and applies them in your
                personal project chat and when you refine an insight. Team chat
                is unaffected. Click a rule to edit it.
              </p>
            </div>
            <label class="alice-prof__switch">
              <input
                type="checkbox"
                :checked="rulebook.enabled !== false"
                @change="toggleRulebook($event.target.checked)"
              />
              <span>Apply my preferences</span>
            </label>
          </div>

          <div v-if="suggestedRules.length" class="alice-prof__rules-group">
            <div class="alice-prof__rules-group-title">
              Suggested — not applied until you accept
            </div>
            <ul class="alice-prof__rules-list">
              <li
                v-for="r in suggestedRules"
                :key="r.id"
                class="alice-prof__rule alice-prof__rule--suggested"
              >
                <span class="alice-prof__rule-cat">{{
                  categoryLabel(r.category)
                }}</span>
                <span class="alice-prof__rule-text">{{ r.text }}</span>
                <span class="alice-prof__rule-actions">
                  <button
                    type="button"
                    class="alice-prof__rule-btn"
                    title="Accept"
                    @click="setStatus(r, 'active')"
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    class="alice-prof__rule-btn"
                    title="Dismiss"
                    @click="setStatus(r, 'dismissed')"
                  >
                    ×
                  </button>
                </span>
              </li>
            </ul>
          </div>

          <div
            v-for="g in ruleGroups"
            :key="g.category"
            class="alice-prof__rules-group"
          >
            <div class="alice-prof__rules-group-title">
              {{ g.label }}
              <span class="alice-prof__rules-count">{{ g.rules.length }}</span>
            </div>
            <ul class="alice-prof__rules-list">
              <li
                v-for="r in g.rules"
                :key="r.id"
                class="alice-prof__rule"
                :class="{ 'alice-prof__rule--paused': r.status === 'retired' }"
              >
                <input
                  v-if="editingId === r.id"
                  v-model="editingText"
                  class="alice-prof__input alice-prof__rule-input"
                  type="text"
                  maxlength="140"
                  @keydown.enter.prevent="commitEdit(r)"
                  @keydown.esc.prevent="cancelEdit"
                  @blur="commitEdit(r)"
                />
                <template v-else>
                  <button
                    type="button"
                    class="alice-prof__rule-text alice-prof__rule-text--btn"
                    title="Click to edit"
                    @click="startEdit(r)"
                  >
                    {{ r.text }}
                  </button>
                  <span class="alice-prof__rule-meta">
                    <span class="alice-prof__rule-badge">{{
                      sourceLabel(r)
                    }}</span>
                    <span v-if="r.hits > 1" class="alice-prof__rule-hits">
                      ×{{ r.hits }}
                    </span>
                  </span>
                  <span class="alice-prof__rule-actions">
                    <button
                      type="button"
                      class="alice-prof__rule-btn"
                      :title="r.status === 'retired' ? 'Resume' : 'Pause'"
                      @click="
                        setStatus(
                          r,
                          r.status === 'retired' ? 'active' : 'retired'
                        )
                      "
                    >
                      {{ r.status === "retired" ? "▶" : "❚❚" }}
                    </button>
                    <button
                      type="button"
                      class="alice-prof__rule-btn"
                      title="Remove"
                      @click="setStatus(r, 'dismissed')"
                    >
                      ×
                    </button>
                  </span>
                </template>
                <p v-if="evidenceFor(r)" class="alice-prof__rule-evidence">
                  {{ evidenceFor(r) }}
                </p>
              </li>
            </ul>
          </div>

          <p
            v-if="!ruleGroups.length && !suggestedRules.length"
            class="alice-prof__usage-empty"
          >
            No preferences yet. Alice adds them when you correct her in chat, or
            add one below.
          </p>

          <form class="alice-prof__rule-add" @submit.prevent="submitRule">
            <input
              v-model="newRuleText"
              class="alice-prof__input"
              type="text"
              maxlength="140"
              placeholder="e.g. Keep answers short unless I ask for detail"
            />
            <select
              v-model="newRuleCategory"
              class="alice-prof__input alice-prof__rule-select"
            >
              <option v-for="c in categories" :key="c" :value="c">
                {{ categoryLabel(c) }}
              </option>
            </select>
            <button
              type="submit"
              class="alice-prof__save"
              :disabled="newRuleText.trim().length < 8 || ruleSaving"
            >
              <span>Add rule</span>
            </button>
          </form>

          <div v-if="learnedRules.length" class="alice-prof__rules-footer">
            <button
              v-if="!confirmClear"
              type="button"
              class="alice-prof__rule-link"
              @click="confirmClear = true"
            >
              Remove all learned rules
            </button>
            <template v-else>
              <span>
                Remove {{ learnedRules.length }} learned rule(s)? Seeded rules
                and your own stay.
              </span>
              <button
                type="button"
                class="alice-prof__rule-link"
                @click="clearLearned"
              >
                Yes, remove
              </button>
              <button
                type="button"
                class="alice-prof__rule-link"
                @click="confirmClear = false"
              >
                Cancel
              </button>
            </template>
          </div>
        </section>

        <section class="alice-prof__usage">
          <div class="alice-prof__usage-head">
            <div class="alice-prof__usage-head-main">
              <div class="alice-prof__label">My AI usage</div>
              <p class="alice-prof__help">
                Estimated usage of Alice's AI models.
                <template v-if="usageSince">
                  Tracking since {{ formatSince(usageSince) }}.</template
                >
                {{ " " }}
                <a
                  :href="energySourceUrl"
                  target="_blank"
                  rel="noopener"
                  class="alice-prof__usage-source"
                  >About the energy estimate</a
                >
              </p>
            </div>
            <div
              v-if="usageDays.length"
              class="alice-prof__windows"
              role="tablist"
              aria-label="Usage window"
            >
              <button
                v-for="w in usageWindows"
                :key="w.key"
                type="button"
                class="alice-prof__window"
                :class="{
                  'alice-prof__window--active': usageWindow === w.value,
                }"
                @click="usageWindow = w.value"
              >
                {{ w.label }}
              </button>
            </div>
          </div>

          <div v-if="usageLoading" class="alice-prof__usage-empty">
            Loading usage…
          </div>
          <div v-else-if="!usageDays.length" class="alice-prof__usage-empty">
            No usage recorded yet. Usage shows up here after your next chat.
          </div>
          <template v-else>
            <div class="alice-prof__usage-kpis">
              <div
                v-for="tile in usageKpis"
                :key="tile.label"
                class="alice-prof__usage-kpi"
              >
                <div class="alice-prof__usage-kpi-value">
                  <Icon
                    v-if="tile.icon"
                    :name="tile.icon"
                    :size="14"
                    class="alice-prof__usage-kpi-icon"
                  />
                  {{ tile.value }}
                </div>
                <div class="alice-prof__usage-kpi-label">{{ tile.label }}</div>
              </div>
            </div>

            <div class="alice-prof__usage-block">
              <div class="alice-prof__usage-block-label">Tokens per day</div>
              <div v-if="usageTrend.length" class="alice-prof__usage-trend">
                <div
                  v-for="d in usageTrend"
                  :key="d.date"
                  class="alice-prof__usage-trend-bar"
                  :title="`${formatSince(d.date)} · ${d.tokens.toLocaleString(
                    'en-GB'
                  )} tokens`"
                >
                  <span
                    class="alice-prof__usage-trend-fill"
                    :style="{ height: `${usageTrendHeight(d.tokens)}%` }"
                  />
                </div>
              </div>
              <div v-else class="alice-prof__usage-empty">
                No usage in this window.
              </div>
            </div>

            <div v-if="usageModels.length" class="alice-prof__usage-block">
              <div class="alice-prof__usage-block-label">By model</div>
              <ul class="alice-prof__usage-bars">
                <li
                  v-for="m in usageModels"
                  :key="m.model"
                  class="alice-prof__usage-bar-row"
                >
                  <span class="alice-prof__usage-bar-label">
                    <span
                      class="alice-prof__eco-badge"
                      :class="`alice-prof__eco-badge--${m.eco}`"
                    >
                      <Icon name="leaf" :size="12" />
                    </span>
                    {{ m.title }}
                  </span>
                  <span class="alice-prof__usage-bar-track">
                    <span
                      class="alice-prof__usage-bar-fill"
                      :style="{
                        width: `${(m.tokens / usageModels[0].tokens) * 100}%`,
                      }"
                    />
                  </span>
                  <span class="alice-prof__usage-bar-value">
                    {{ formatTokens(m.tokens) }}
                  </span>
                </li>
              </ul>
            </div>
          </template>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";

import TopBar from "@/components/alice/TopBar.vue";
import Sidebar from "@/components/alice/Sidebar.vue";
import Avatar from "@/components/alice/Avatar.vue";
import Icon from "@/components/alice/Icon.vue";
import { colorForOwner } from "@/components/alice/normalize";
import { RULE_CATEGORIES } from "@/utils/rulebook";

const CATEGORY_LABELS = {
  language: "Language",
  style: "Style",
  format: "Format",
  content: "Content",
  process: "Process",
};

// Display names + eco tiers for known model ids — mirrors MODEL_OPTIONS in
// ProjectChat.vue. Unknown ids fall back to the raw id at "medium".
const MODEL_LABELS = {
  "claude-opus-5-5": { title: "Claude Opus 5.5", eco: "medium" },
  "claude-opus-5": { title: "Claude Opus 5", eco: "medium" },
  "claude-opus-4-8": { title: "Claude Opus 4.8", eco: "medium" },
  "claude-sonnet-5-5": { title: "Claude Sonnet 5.5", eco: "low" },
  "claude-sonnet-5": { title: "Claude Sonnet 5", eco: "low" },
  "claude-sonnet-4-6": { title: "Claude Sonnet 4.6", eco: "low" },
  "claude-haiku-4-5": { title: "Claude Haiku 4.5", eco: "low" },
  "gpt-6-sol": { title: "GPT-6 Sol", eco: "medium" },
  "gpt-6-luna": { title: "GPT-6 Luna", eco: "low" },
  "gpt-5.6-sol": { title: "GPT-5.6 Sol", eco: "medium" },
  "gemini-3.1-pro-preview": { title: "Gemini 3.1 Pro", eco: "medium" },
  "gemini-3.8-flash": { title: "Gemini 3.8 Flash", eco: "low" },
  // Voice messages in chat.
  "gpt-4o-mini-transcribe": { title: "GPT-4o mini Transcribe", eco: "low" },
};

// Independent per-query energy methodology (Epoch AI). Anthropic publishes no
// per-query figure; this is the most reputable average calculation available.
const ENERGY_SOURCE_URL =
  "https://epoch.ai/gradient-updates/how-much-energy-does-chatgpt-use";

export default {
  name: "ProfilePage",
  components: { TopBar, Sidebar, Avatar, Icon },
  data() {
    return {
      job_title: "",
      initialJobTitle: "",
      saving: false,
      usageDays: [],
      usageLoading: false,
      usageWindow: 30,
      energySourceUrl: ENERGY_SOURCE_URL,
      categories: RULE_CATEGORIES,
      editingId: null,
      editingText: "",
      newRuleText: "",
      newRuleCategory: "style",
      ruleSaving: false,
      confirmClear: false,
      usageWindows: [
        { key: "7", label: "7d", value: 7 },
        { key: "30", label: "30d", value: 30 },
        { key: "90", label: "90d", value: 90 },
        { key: "all", label: "All", value: 0 },
      ],
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user || {},
      rulebook: (state) => state.rulebook,
      isGuest: (state) => state.guest === true,
      projects: (state) => state.projects || [],
    }),
    rules() {
      return (this.rulebook && this.rulebook.rules) || [];
    },
    suggestedRules() {
      return this.rules.filter((r) => r.status === "suggested");
    },
    learnedRules() {
      return this.rules.filter(
        (r) => r.source === "learned" && r.status !== "dismissed"
      );
    },
    ruleGroups() {
      const shown = this.rules.filter(
        (r) => r.status === "active" || r.status === "retired"
      );
      return this.categories
        .map((category) => ({
          category,
          label: CATEGORY_LABELS[category] || category,
          rules: shown
            .filter((r) => (r.category || "style") === category)
            .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)),
        }))
        .filter((g) => g.rules.length);
    },
    userName() {
      return this.user?.displayName || this.user?.email || "";
    },
    userPhoto() {
      return this.user?.photoURL || "";
    },
    ownerColor() {
      return colorForOwner(this.userName);
    },
    dirty() {
      return this.job_title !== this.initialJobTitle;
    },
    usageSince() {
      return this.usageDays.length ? this.usageDays[0].date : null;
    },
    filteredUsageDays() {
      if (!this.usageWindow) return this.usageDays;
      const cutoff = new Date(Date.now() - (this.usageWindow - 1) * 86400000);
      const key = cutoff.toISOString().slice(0, 10);
      return this.usageDays.filter((d) => d.date >= key);
    },
    usageKpis() {
      const t = { tokens: 0, energyWh: 0, co2g: 0 };
      this.filteredUsageDays.forEach((d) => {
        t.tokens += (d.inputTokens || 0) + (d.outputTokens || 0);
        t.energyWh += d.energyWh || 0;
        t.co2g += d.co2g || 0;
      });
      return [
        { label: "Tokens", value: this.formatTokens(t.tokens) },
        { label: "Energy", value: `${t.energyWh.toFixed(1)} Wh` },
        { label: "CO₂", value: `${t.co2g.toFixed(1)} g`, icon: "leaf" },
      ];
    },
    usageTrend() {
      const map = {};
      this.filteredUsageDays.forEach((d) => {
        map[d.date] = {
          tokens: (d.inputTokens || 0) + (d.outputTokens || 0),
        };
      });
      if (!this.usageWindow) {
        return Object.keys(map)
          .sort()
          .map((date) => ({ date, ...map[date] }));
      }
      // Pad missing days with zeros so the bar strip is gap-free.
      const out = [];
      const today = new Date();
      for (let i = this.usageWindow - 1; i >= 0; i--) {
        const dt = new Date(today.getTime() - i * 86400000);
        const key = dt.toISOString().slice(0, 10);
        out.push({ date: key, ...(map[key] || { tokens: 0 }) });
      }
      return out;
    },
    usageTrendMax() {
      return this.usageTrend.reduce((m, d) => Math.max(m, d.tokens), 0);
    },
    usageModels() {
      const merged = {};
      this.filteredUsageDays.forEach((d) => {
        Object.values(d.models || {}).forEach((m) => {
          if (!m || typeof m !== "object") return;
          const id = m.model || "unknown";
          if (!merged[id]) merged[id] = { model: id, tokens: 0 };
          merged[id].tokens += (m.inputTokens || 0) + (m.outputTokens || 0);
        });
      });
      return Object.values(merged)
        .map((m) => ({
          ...m,
          title: MODEL_LABELS[m.model]?.title || m.model,
          eco: MODEL_LABELS[m.model]?.eco || "medium",
        }))
        .sort((a, b) => b.tokens - a.tokens);
    },
  },
  watch: {
    "user.job_title": {
      immediate: true,
      handler(value) {
        const next = value || "";
        this.job_title = next;
        this.initialJobTitle = next;
      },
    },
    "user.uid": {
      immediate: true,
      handler(uid) {
        if (uid) this.loadUsage(uid);
      },
    },
  },
  mounted() {
    if (!this.rulebook && !this.isGuest) this.$store.dispatch("fetchRulebook");
  },
  methods: {
    categoryLabel(c) {
      return CATEGORY_LABELS[c] || c;
    },
    sourceLabel(r) {
      if (r.source === "user") return "Added by you";
      if (r.source === "seed") return "Seeded";
      return "Learned";
    },
    evidenceFor(r) {
      const ev = (r.evidence || []).find((e) => e && e.quote);
      if (!ev) return "";
      const project = this.projects.find((p) => p.uid === ev.projectId);
      const where = project ? `In “${project.title}” you said` : "You said";
      return `${where}: “${ev.quote}”`;
    },
    toggleRulebook(enabled) {
      this.$store.dispatch("setRulebookEnabled", enabled);
    },
    setStatus(r, status) {
      this.$store.dispatch("setRuleStatus", { id: r.id, status });
    },
    startEdit(r) {
      this.editingId = r.id;
      this.editingText = r.text;
      this.$nextTick(() => {
        const el = this.$el.querySelector(".alice-prof__rule-input");
        if (el) el.focus();
      });
    },
    cancelEdit() {
      this.editingId = null;
      this.editingText = "";
    },
    async commitEdit(r) {
      if (this.editingId !== r.id) return;
      const text = this.editingText.trim();
      this.editingId = null;
      if (text && text !== r.text) {
        await this.$store.dispatch("updateRuleText", { id: r.id, text });
      }
      this.editingText = "";
    },
    async submitRule() {
      const text = this.newRuleText.trim();
      if (text.length < 8) return;
      this.ruleSaving = true;
      try {
        await this.$store.dispatch("addRule", {
          text,
          category: this.newRuleCategory,
        });
        this.newRuleText = "";
      } finally {
        this.ruleSaving = false;
      }
    },
    async clearLearned() {
      this.confirmClear = false;
      await this.$store.dispatch("dismissLearnedRules");
    },
    async loadUsage(uid) {
      this.usageLoading = true;
      try {
        const snap = await getDocs(collection(db, "usage", uid, "dailyUsage"));
        this.usageDays = snap.docs
          .map((d) => d.data())
          .filter((d) => d && d.date)
          .sort((a, b) => (a.date < b.date ? -1 : 1));
      } catch (e) {
        console.error("Failed to load usage:", e);
        this.usageDays = [];
      } finally {
        this.usageLoading = false;
      }
    },
    usageTrendHeight(tokens) {
      if (!this.usageTrendMax) return 0;
      return Math.max(2, Math.round((tokens / this.usageTrendMax) * 100));
    },
    formatTokens(n) {
      if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
      if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
      return `${Math.round(n)}`;
    },
    formatSince(iso) {
      return new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    async updateUser() {
      if (!this.user?.uid) return;
      this.saving = true;
      try {
        await updateDoc(doc(db, "users", this.user.uid), {
          job_title: this.job_title,
        });
        this.initialJobTitle = this.job_title;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.alice-shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 56px 1fr;
  height: 100vh;
  width: 100%;
  background: var(--bg-muted);
  font-family: var(--font-body);
  color: var(--fg-1);
  overflow: hidden;
}
.alice-shell__topbar {
  grid-column: 1 / -1;
  grid-row: 1;
}
.alice-shell__sidebar {
  grid-column: 1;
  grid-row: 2;
}
.alice-shell__main {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: var(--vp-white);
}

.alice-tb-crumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: var(--vp-grijs-80);
  white-space: nowrap;
}
.alice-tb-crumb:hover {
  color: var(--vp-white);
}
.alice-tb-crumb--current {
  color: var(--vp-white);
  text-transform: none;
  letter-spacing: 0.04em;
}
.alice-tb-sep {
  color: var(--vp-grijs-160);
  font-weight: 400;
}

/* ============== Page ============== */
.alice-prof {
  --prof-gutter: 32px;
  --prof-col: 880px;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* Every band runs edge to edge (hairlines, muted identity background) but
   keeps its content in one left-aligned column of --prof-col. */
.alice-prof__head,
.alice-prof__identity,
.alice-prof__form,
.alice-prof__rules,
.alice-prof__usage {
  flex-shrink: 0;
  padding-left: var(--prof-gutter);
  padding-right: max(
    var(--prof-gutter),
    calc(100% - var(--prof-col) - var(--prof-gutter))
  );
}

.alice-prof__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding-top: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-prof__head-main {
  flex: 1;
  min-width: 0;
}
.alice-prof__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-prof__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-prof__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 560px;
  line-height: 1.5;
}
.alice-prof__save {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 20px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-prof__save:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-prof__save:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ============== Identity card ============== */
.alice-prof__identity {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-top: 28px;
  padding-bottom: 28px;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--bg-muted);
}
.alice-prof__identity-text {
  min-width: 0;
}
.alice-prof__identity-name {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--vp-black);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-prof__identity-email {
  margin-top: 4px;
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--fg-3);
  letter-spacing: 0.02em;
}

/* ============== Form ============== */
.alice-prof__form {
  padding-top: 32px;
  padding-bottom: 32px;
}
.alice-prof__field {
  display: flex;
  flex-direction: column;
  max-width: 560px;
}
.alice-prof__label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-prof__input {
  height: 48px;
  padding: 0 14px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--vp-black);
  outline: none;
  border-radius: 0;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
/* ============== Rulebook ============== */
.alice-prof__rules {
  padding-top: 32px;
  padding-bottom: 32px;
  border-top: 1px solid var(--border-hairline);
}
.alice-prof__switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-1);
  white-space: nowrap;
  cursor: pointer;
}
.alice-prof__rules-group {
  margin-top: 28px;
}
.alice-prof__usage-head + .alice-prof__rules-group {
  margin-top: 4px;
}
.alice-prof__rules-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-2);
  margin-bottom: 8px;
}
.alice-prof__rules-count {
  font-weight: 400;
  color: var(--fg-3);
}
.alice-prof__rules-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border-hairline);
}
.alice-prof__rule {
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-template-areas: "text meta actions" "evidence evidence evidence";
  align-items: center;
  gap: 4px 14px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-prof__rule--paused .alice-prof__rule-text {
  color: var(--fg-3);
  text-decoration: line-through;
}
.alice-prof__rule--suggested {
  grid-template-columns: auto 1fr auto;
  grid-template-areas: "cat text actions";
}
.alice-prof__rule-cat {
  grid-area: cat;
  font-family: var(--font-ui);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--fg-3);
}
.alice-prof__rule-text {
  grid-area: text;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--fg-1);
  line-height: 1.4;
  text-align: left;
}
.alice-prof__rule-text--btn {
  background: transparent;
  border: none;
  padding: 0;
  cursor: text;
}
.alice-prof__rule-text--btn:hover {
  text-decoration: underline dotted;
}
.alice-prof__rule-input {
  grid-area: text;
  height: 36px;
  font-size: 15px;
}
.alice-prof__rule-meta {
  grid-area: meta;
  display: inline-flex;
  gap: 8px;
  align-items: center;
}
.alice-prof__rule-badge,
.alice-prof__rule-hits {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
  white-space: nowrap;
}
.alice-prof__rule-actions {
  grid-area: actions;
  display: inline-flex;
  gap: 2px;
}
.alice-prof__rule-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--fg-3);
  font-size: 13px;
}
.alice-prof__rule-btn:hover {
  color: var(--vp-black);
  background: var(--bg-muted);
}
.alice-prof__rule-evidence {
  grid-area: evidence;
  margin: 0;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--fg-3);
}
.alice-prof__rule-add {
  display: grid;
  grid-template-columns: 1fr 160px auto;
  gap: 10px;
  margin-top: 24px;
}
.alice-prof__rule-add .alice-prof__save {
  height: 48px;
}
.alice-prof__rule-select {
  padding-right: 8px;
}
.alice-prof__rules-footer {
  margin-top: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--fg-2);
}
.alice-prof__rule-link {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-2);
  text-decoration: underline;
}
.alice-prof__input::placeholder {
  color: var(--fg-3);
}
.alice-prof__input:focus {
  border-color: var(--vp-black);
  box-shadow: inset 0 -2px 0 0 var(--accent-100);
}
.alice-prof__help {
  margin: 8px 0 0;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}

/* ============== AI usage ============== */
.alice-prof__usage {
  padding-top: 32px;
  padding-bottom: 40px;
  border-top: 1px solid var(--border-hairline);
}
.alice-prof__usage-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}
.alice-prof__usage-head-main {
  min-width: 0;
}
.alice-prof__usage-head-main .alice-prof__help {
  max-width: 600px;
  line-height: 1.5;
}
.alice-prof__usage-empty {
  padding: 24px 0;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--fg-3);
}

/* Window switcher (mirrors the AdminAnalytics one) */
.alice-prof__windows {
  display: inline-flex;
  border: 1px solid var(--border-hairline);
  height: 32px;
  flex-shrink: 0;
}
.alice-prof__window {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  padding: 0 12px;
  height: 100%;
  background: var(--vp-white);
  border: none;
  border-right: 1px solid var(--border-hairline);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-2);
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-prof__window:last-child {
  border-right: none;
}
.alice-prof__window:hover {
  background: var(--bg-muted);
  color: var(--vp-black);
}
.alice-prof__window--active,
.alice-prof__window--active:hover {
  background: var(--vp-black);
  color: var(--vp-white);
}

.alice-prof__usage-source {
  color: var(--vp-blauw-120);
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* KPI row */
.alice-prof__usage-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border: 1px solid var(--border-hairline);
  margin-bottom: 24px;
}
.alice-prof__usage-kpi {
  padding: 16px 18px;
  border-right: 1px solid var(--border-hairline);
}
.alice-prof__usage-kpi:last-child {
  border-right: none;
}
.alice-prof__usage-kpi-value {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1;
  color: var(--vp-black);
}
.alice-prof__usage-kpi-icon {
  color: var(--vp-groen-100);
}
.alice-prof__usage-kpi-label {
  margin-top: 6px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}

/* Blocks */
.alice-prof__usage-block {
  margin-bottom: 24px;
}
.alice-prof__usage-block:last-child {
  margin-bottom: 0;
}
.alice-prof__usage-block-label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 12px;
}

/* Trend sparkbars (mirrors alice-an__trend) */
.alice-prof__usage-trend {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 72px;
}
.alice-prof__usage-trend-bar {
  flex: 1;
  min-width: 3px;
  height: 100%;
  display: flex;
  align-items: flex-end;
  background: var(--bg-muted);
}
.alice-prof__usage-trend-fill {
  display: block;
  width: 100%;
  background: var(--vp-black);
  transition: height 0.2s ease;
}
.alice-prof__usage-trend-bar:hover .alice-prof__usage-trend-fill {
  background: var(--accent-100);
}

/* Per-model bars (mirrors alice-an__bars) */
.alice-prof__usage-bars {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.alice-prof__usage-bar-row {
  display: grid;
  grid-template-columns: minmax(150px, 200px) 1fr minmax(110px, auto);
  align-items: center;
  gap: 12px;
}
.alice-prof__usage-bar-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-prof__usage-bar-track {
  position: relative;
  display: block;
  height: 8px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
}
.alice-prof__usage-bar-fill {
  position: absolute;
  inset: 0 auto 0 0;
  background: var(--vp-black);
  transition: width 0.2s ease;
}
.alice-prof__usage-bar-value {
  text-align: right;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--fg-2);
  white-space: nowrap;
}

/* Eco badge colors mirror alice-chat__eco-badge in ProjectChat.vue */
.alice-prof__eco-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.alice-prof__eco-badge--low {
  color: var(--vp-groen-100);
}
.alice-prof__eco-badge--medium {
  color: var(--vp-geel-120);
}

@media (max-width: 1279px) {
  .alice-prof {
    --prof-gutter: 20px;
  }
  .alice-prof__form,
  .alice-prof__rules,
  .alice-prof__usage {
    padding-top: 24px;
    padding-bottom: 24px;
  }
  .alice-prof__usage-head {
    flex-direction: column;
  }
  .alice-prof__usage-kpis {
    grid-template-columns: 1fr;
  }
  .alice-prof__usage-kpi {
    border-right: none;
    border-bottom: 1px solid var(--border-hairline);
  }
  .alice-prof__usage-kpi:last-child {
    border-bottom: none;
  }
}
</style>
